export interface ICustomerSkuRelatedJob {
  id?: string;
  number?: string;
}

export interface ICustomerSkuItem {
  id?: string;
  sku?: string;
  serialNumber?: string;
  numberOfFiles?: number;
  isExistInJob?: boolean;
}

export interface ICustomerSku {
  id?: string;
  printHouseId?: string;
  customerId?: string;
  customerName?: string;
  sku?: string;
  skuGroupName?: string;
  isSKUGroupNameEditable?: boolean;
  isExist?: boolean;
  isExistInJob?: boolean;
  boardMissionId?: string;
  productId?: string;
  productName?: string;
  details?: string;
  unitPrice?: number | string;
  relatedJobs?: ICustomerSkuRelatedJob[];
  filesCount?: number;
  quantity?: number;
  createdDate?: string;
  startDate?: string;
  expirationDate?: string;
  serialNumber?: string;
  orderItemId?: string;
  productType?: string;
  skUs?: ICustomerSkuItem[];
  isSkuGroup?: boolean;
}

const readIdValue = (value: unknown): string => {
  if (typeof value === "string" || typeof value === "number") {
    const text = String(value).trim();
    if (!text || text === "null" || text === "undefined") {
      return "";
    }
    return text;
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return (
      readIdValue(record.value) ||
      readIdValue(record.id) ||
      readIdValue(record.Id) ||
      readIdValue(record.clientId) ||
      readIdValue(record.ClientId)
    );
  }
  return "";
};

/** get-all customer SKUs returns the customer id on `clientId`. */
export const readCustomerId = (
  item: Record<string, unknown> | null | undefined
): string => {
  if (!item) {
    return "";
  }
  return (
    readIdValue(item.clientId) ||
    readIdValue(item.ClientId) ||
    readIdValue(item.clientID) ||
    readIdValue(item.ClientID) ||
    readIdValue(item.customerId) ||
    readIdValue(item.CustomerId) ||
    readIdValue(item.customerID) ||
    readIdValue(item.CustomerID)
  );
};

export const parseBooleanFlag = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
    return undefined;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "yes" || normalized === "1") {
      return true;
    }
    if (normalized === "false" || normalized === "no" || normalized === "0") {
      return false;
    }
  }
  return undefined;
};

export const parseIsSkuGroupNameEditable = (
  value: unknown
): boolean | undefined => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
    return undefined;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "yes" || normalized === "1") {
      return true;
    }
    if (normalized === "false" || normalized === "no" || normalized === "0") {
      return false;
    }
  }
  return undefined;
};

export interface IGetAllCustomerSkusRequest {
  search?: string;
  customerId?: string | null;
  productId?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  dataTableSettings: {
    pageNumber: number;
    pageSize: number;
    order: { column: number; dir: string }[];
  };
}

export interface IAddCustomerSkusRequest {
  skuGroupName: string;
  skUs: string[];
  boardMissionId: string;
  startDate?: string | null;
  expirationDate?: string | null;
}

export interface IUpdateCustomerSkuDateRequest {
  skuGroupName: string;
  date: string | null;
}

export interface IUpdateCustomerSkuSerialNumberRequest {
  id: string;
  serialNumber: string;
}

export interface IReorderCustomerSkuItemRequest {
  boardMissionId: string;
  orderItemId: string;
  quantity: number;
  customerSkuId?: string;
}

export interface ICreateQuoteFromSkuReorderRequest {
  boardMissionId: string;
  orderItemId: string;
  quantity: number;
  keepUnitPrice: boolean;
  items: IReorderCustomerSkuItemRequest[];
}

export interface IWorkTypeReorderItem {
  name: string;
  quantity: number;
}

export interface ICreateItemsFromWorkTypesRequest {
  orderItemId: string;
  documentId?: string;
  clientId?: string;
  price: number;
  recalculatePrice: boolean;
  workTypes: IWorkTypeReorderItem[];
  skuGroupName?: string;
}
